<script lang="ts">
  import { goto } from '$app/navigation';
  import { page } from '$app/state';

  import { Button, GridView, Alert } from '$shared/components';
  import type { AlertDetailedInfo } from '$provider/models/AlertDetailedInfo';
  import { AlertOption } from '$shared/models/AlertOption';
  import { ReviewType } from '$provider/constants/ReviewType';
  import { ButtonStyle } from '$shared/constants/ButtonStyle';

  interface Props {
    entityName?: string;
    type?: ReviewType;
    alert?: string;
    invert?: boolean;
    error?: AlertDetailedInfo | null | undefined | string;
    alertDate?: string;
    cancelLabel?: string;
    disableCancelBtn?: boolean;
    titleStyle?: ButtonStyle;
    dataAttributeName?: string;
    actions?: import('svelte').Snippet;
    children?: import('svelte').Snippet;
    oncancel?: () => void;
    oncloseerror?: () => void;
    ongotoentity?: () => void;
  }

  let {
    entityName = '',
    type = ReviewType.None,
    alert = '',
    invert = false,
    error = '',
    alertDate = '',
    cancelLabel = '',
    disableCancelBtn = false,
    titleStyle = ButtonStyle.Link,
    dataAttributeName = '',
    actions,
    children,
    oncancel,
    oncloseerror,
    ongotoentity
  }: Props = $props();
  export const scrollToTop = (): void => {
    parentDiv.scrollIntoView({ behavior: 'smooth' });
  };

  let parentDiv: HTMLDivElement = $state();

  const onClickCancel = (): void => {
    if (page.url.searchParams.has('ll')) {
      goto(page.url.searchParams.get('ll'));
      return;
    }
    oncancel?.();
  };

  const onCloseError = (): void => {
    oncloseerror?.();
  };

  const onClickTitle = (): void => {
    ongotoentity?.();
  };
</script>

<div bind:this={parentDiv} data-cy={`${dataAttributeName}-review`} class="review-layout">
  <div data-cy={`${dataAttributeName}-header`} class="header">
    <div class="cancel">
      <Button disabled={disableCancelBtn} name="cancel" onclick={onClickCancel}>
        {cancelLabel || 'Cancel'}
      </Button>
    </div>
    <div data-cy={`${dataAttributeName}-title`} class="title">
      {#if entityName}
        <Button name={`${dataAttributeName}-entity-btn`} style={titleStyle} onclick={onClickTitle}>
          {entityName}
        </Button>
        {#if type}
          <div class="type">{type}</div>
        {/if}
      {/if}
    </div>
    <div class="actions">
      {@render actions?.()}
    </div>
  </div>
  <div class="container" class:invert>
    <GridView>
      {#if error}
        <div class="error">
          <Alert
            options={AlertOption.errorAlert()}
            data={error}
            maxWidth
            closable
            onclose={onCloseError}
          />
        </div>
      {/if}
      {#if alert}
        <div data-cy={`${dataAttributeName}-status-card`} class="alert">
          <div data-cy="status-text" class="text">{alert}</div>
          {#if alertDate}
            <div data-cy="status-date" class="date">{alertDate}</div>
          {/if}
        </div>
      {/if}
      <div class="context">
        {@render children?.()}
      </div>
    </GridView>
  </div>
</div>

<style>
  .error {
    width: 100%;
    grid-column: 1 / span 12;
    margin-bottom: 16px;
  }

  .header {
    width: 100%;
    background-color: #ffffff;
    box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    justify-content: space-between;
    height: 56px;
    padding: 0 24px;
    position: absolute;
    top: 0;
    left: 0;
  }

  .cancel,
  .actions {
    display: flex;
    align-items: center;
  }

  .actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }

  .title {
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 14px;
    line-height: 17px;
  }
  .title .type:before {
    content: '/';
    margin-right: 8px;
  }
  .container {
    background-color: var(--grey-bg);
    min-width: 100%;
    min-height: 100vh;
    padding: calc(56px + 24px) 24px 0;
  }
  .container.invert {
    background-color: #fff;
  }
  .context,
  .alert {
    grid-column: 1 / span 12;
  }
  .alert {
    background-color: #ffffff;
    border: 1px solid #0d54f5;
    box-shadow: 0px 4px 14px rgba(0, 0, 0, 0.1);
    border-radius: 3px;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 16px;
    margin-bottom: 32px;
  }
  .alert .text {
    font-weight: 500;
    font-size: 16px;
    line-height: 23px;
    color: var(--blue);
  }
  .alert .date {
    font-weight: normal;
    font-size: 12px;
    line-height: 23px;
    color: var(--grey-medium);
    margin-left: 16px;
  }
</style>
